import { useEditProtection } from '../../shared/components/EditProtection';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import './CashSpending.css';

interface User {
  id: number;
  name: string;
  total_cash: number;
}

const CashSpending: React.FC = () => {
  const navigate = useNavigate();
  const { user, sessionToken } = useAuth();
  const { showToast, fetchNotifications } = useNotification();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const categories = [
    'General',
    'Food & Dining',
    'Transportation',
    'Shopping',
    'Bills & Utilities',
    'Entertainment',
    'Business Expenses',
    'Office Supplies',
    'Maintenance',
    'Other'
  ];

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/users', {
        headers: {
          'X-Session-Token': sessionToken || ''
        },
        cache: 'no-store'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const [saved, setSaved] = useState(false);
  useEditProtection(!saved && (!!amount || !!description));
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    // Only validate user selection for Admin/Manager
    if (user && ['Owner', 'Admin', 'Manager'].includes(user.role) && !selectedUserId) {
      newErrors.selectedUserId = 'Please select a user';
    }

    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }

    // Check if user has enough cash
    const targetUser = selectedUserId ? users.find(u => u.id === selectedUserId) : users.find(u => u.id === user?.id);
    if (targetUser && parseFloat(amount) > targetUser.total_cash) {
      newErrors.amount = `Insufficient balance. Available: $${(Number(targetUser.total_cash) || 0).toFixed(2)}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!window.confirm(`Confirm $${Number(amount).toFixed(2)} for ${users.find(u => u.id === selectedUserId)?.name || user?.name}?`)) return;
    setIsSubmitting(true);

    try {
      const requestBody: any = {
        amount: parseFloat(amount),
        description: description.trim(),
        category: category || 'General'
      };

      // If Admin/Manager selected a different user, include performed_by_user_id
      if (selectedUserId && user && ['Owner', 'Admin', 'Manager'].includes(user.role)) {
        requestBody.performed_by_user_id = selectedUserId;
      }

      const response = await fetch('/api/cash-spending', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken || ''
        },
        body: JSON.stringify(requestBody),
        cache: 'no-store'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to record cash spending');
      }

      const data = await response.json();
      showToast({
        type: 'success',
        title: 'Cash Spending',
        message: `${user?.name} spent $${parseFloat(amount).toFixed(2)} on ${description.trim()}`
      });
      fetchNotifications();
      setSaved(true);
      setTimeout(() => navigate('/cash', {
        state: {
          toast: {
            type: 'success',
            title: 'Cash Spending Recorded',
            message: data.message,
          },
        },
      }), 0);
    } catch (error) {
      console.error('Error recording cash spending:', error);
      showToast({
        type: 'error',
        title: 'Error',
        message: `Failed to record cash spending: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  // For Admin/Manager, use selected user; for Employee, use current user
  const targetUserId = (user && ['Owner', 'Admin', 'Manager'].includes(user.role) && selectedUserId) ? selectedUserId : user?.id;
  const selectedUserData = users.find(u => u.id === targetUserId);

  return (
    <div className="cash-spending">
      <div className="header">
        <button onClick={() => navigate('/cash/new')} className="back-btn">← Back</button>
        <h2>Record Cash Spending</h2>
      </div>

      <div className="spending-info">
        <p>Record how you spent cash that was previously withdrawn from the till.</p>
      </div>

      <form onSubmit={handleSubmit} className="spending-form">
        {user && ['Owner', 'Admin', 'Manager'].includes(user.role) && (
          <div className="form-group">
            <label htmlFor="selectedUserId">Who spent the cash? *</label>
            <select
              id="selectedUserId"
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
              className={errors.selectedUserId ? 'error' : ''}
            >
              <option value="">Select a user</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} (Balance: ${(Number(u.total_cash) || 0).toFixed(2)})
                </option>
              ))}
            </select>
            {errors.selectedUserId && <span className="error-text">{errors.selectedUserId}</span>}
            {users.length === 0 && (
              <small className="info-text">No users found.</small>
            )}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="amount">Amount Spent (CAD) *</label>
          <div className="amount-input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="text" inputMode="decimal"
              id="amount"
              value={amount}
              onChange={handleAmountChange}
              className={errors.amount ? 'error amount-input' : 'amount-input'}
              placeholder="0.00"
              maxLength={10}
            />
          </div>
          {errors.amount && <span className="error-text">{errors.amount}</span>}
          {selectedUserData && (
            <small className="balance-info">
              Available balance: ${(Number(selectedUserData.total_cash) || 0).toFixed(2)}
            </small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="description">What was purchased? *</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={errors.description ? 'error' : ''}
            placeholder="e.g., Office supplies, lunch for team, taxi fare..."
            rows={3}
            maxLength={500}
          />
          {errors.description && <span className="error-text">{errors.description}</span>}
          <small className="char-count">{description.length}/500 characters</small>
        </div>

        <div className="form-group">
          <label htmlFor="category">Category (Optional)</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="spending-summary">
          <h3>Summary</h3>
          <div className="summary-row">
            <span>User:</span>
            <span>{selectedUserData?.name || user?.name || 'Not selected'}</span>
          </div>
          <div className="summary-row">
            <span>Amount:</span>
            <span className="amount-display">${amount || '0.00'}</span>
          </div>
          <div className="summary-row">
            <span>Description:</span>
            <span>{description || 'Not specified'}</span>
          </div>
          <div className="summary-row">
            <span>Category:</span>
            <span>{category || 'General'}</span>
          </div>
          {selectedUserData && (
            <div className="summary-row">
              <span>Remaining Balance:</span>
              <span className="balance-display">
                ${(selectedUserData.total_cash - parseFloat(amount || '0')).toFixed(2)}
              </span>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="submit"
            disabled={isSubmitting}
            className="submit-btn"
          >
            {isSubmitting ? 'Recording...' : 'Record Spending'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CashSpending;